import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { ExamScore } from '@/types';
import { groupExamScores } from '@/lib/scores';
import ExamSessionCard from '@/pages/students/components/ExamSessionCard';
import ExamSessionForm from '@/pages/students/components/ExamSessionForm';
import ScoreTrendPanel from '@/pages/students/components/ScoreTrendPanel';

interface ExamScoreSectionProps {
  studentId: string;
}

export default function ExamScoreSection({ studentId }: ExamScoreSectionProps) {
  const [scores, setScores] = useState<ExamScore[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await supabase
        .from('exam_scores')
        .select('*')
        .eq('student_id', studentId)
        .order('created_at', { ascending: false });
      if (fetchError) throw fetchError;
      setScores((data as ExamScore[]) ?? []);
    } catch {
      setError('시험 성적을 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    load();
  }, [load]);

  const sessions = groupExamScores(scores);

  return (
    <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
              <i className="ri-bar-chart-box-line"></i>
            </span>
            성적 관리
          </h2>
          <p className="mt-1 text-xs text-foreground-500">
            이미 본 학교시험이나 모의고사의 과목별 점수와 등급·석차를 기록해요.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1.5 rounded-md bg-primary-500 px-3.5 py-2 text-sm font-semibold text-background-50 transition hover:bg-primary-600 whitespace-nowrap"
        >
          <i className={open ? 'ri-close-line' : 'ri-add-line'}></i>
          {open ? '닫기' : '성적 기록하기'}
        </button>
      </div>

      {open && (
        <ExamSessionForm studentId={studentId} onCreated={load} onClose={() => setOpen(false)} />
      )}

      {error && (
        <div className="mt-4 flex items-center gap-2 rounded-md border border-accent-200 bg-accent-100/70 px-3 py-2 text-xs text-accent-800">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10 text-sm text-foreground-500">
          <i className="ri-loader-4-line mr-2 animate-spin"></i>
          불러오는 중...
        </div>
      ) : sessions.length === 0 ? (
        <p className="mt-5 rounded-lg border border-dashed border-background-300 py-10 text-center text-sm text-foreground-500">
          아직 기록한 성적이 없어요. &lsquo;성적 기록하기&rsquo;로 이번 시험 성적을 추가해보세요.
        </p>
      ) : (
        <div className="mt-5 flex flex-col gap-4">
          <ScoreTrendPanel scores={scores} />
          {sessions.map((session) => (
            <ExamSessionCard key={session.key} session={session} onChange={load} />
          ))}
        </div>
      )}
    </section>
  );
}